import type { StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import { VALID_GENRES, VALID_STATUSES } from "@/lib/genre-status";
import { GENRE_REGISTRY } from "@/lib/genres/registry";
import {
    isCrownDiscoveryFilter,
    type CrownDiscoveryFilter,
} from "@/lib/spotlights/discovery-filters";

// ─── URL Param Parsing ───────────────────────────────────
// Shared by browse/page.tsx, genre/[slug]/page.tsx, and tests.

/** Parse a raw URL param into a validated StoryGenre, or undefined. */
export function parseGenre(value: unknown): StoryGenre | undefined {
    if (typeof value === "string" && VALID_GENRES.has(value as StoryGenre)) {
        return value as StoryGenre;
    }
    return undefined;
}

/** Parse a raw URL param into a validated StoryStatus, or undefined. */
export function parseStatus(value: unknown): StoryStatus | undefined {
    if (typeof value === "string" && VALID_STATUSES.has(value as StoryStatus)) {
        return value as StoryStatus;
    }
    return undefined;
}

/** Parse the single Browse-only Crown discovery filter. */
export function parseDiscovery(
    value: unknown,
): CrownDiscoveryFilter | undefined {
    return isCrownDiscoveryFilter(value) ? value : undefined;
}

/** Parse a raw URL param into a validated tag name, or undefined. */
function parseTag(value: unknown): string | undefined {
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        // Allow lowercase alphanumerics plus `_` and `-` so multi-word tag
        // slugs (e.g. `magic-system`, `slice_of_life`) survive a round trip
        // through the URL. Max 30 chars.
        if (/^[a-z0-9_-]{1,30}$/.test(normalized)) {
            return normalized;
        }
    }
    return undefined;
}

// ─── Multi-select param parsing ──────────────────────────
// Repeated query params (`?genre=a&genre=b`) arrive from Next.js as a string
// array; a single occurrence arrives as a plain string. Both collapse to a
// deduped, validated list — invalid entries are silently dropped.

/** Coerce a Next.js searchParam (`string | string[] | undefined`) to a string[]. */
function toArray(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.filter((v): v is string => typeof v === "string");
    }
    if (typeof value === "string") return [value];
    return [];
}

/** Parse a genre searchParam into a deduped list of valid (UI) genres. */
export function parseGenres(value: unknown): StoryGenre[] {
    const out: StoryGenre[] = [];
    for (const raw of toArray(value)) {
        const g = parseGenre(raw);
        if (g && !out.includes(g)) out.push(g);
    }
    return out;
}

/** Parse a tag searchParam into a deduped list of valid tag names. */
export function parseTags(value: unknown): string[] {
    const out: string[] = [];
    for (const raw of toArray(value)) {
        const t = parseTag(raw);
        if (t && !out.includes(t)) out.push(t);
    }
    return out;
}

// ─── Genre slug <-> enum mapping ─────────────────────────
// DB enum values use underscores (`progression_fantasy`); URL slugs use
// dashes (`progression-fantasy`). Identity comes from the genre registry; we
// expose a tight helper here so URL builders never import the registry array.

const ENUM_TO_SLUG = new Map<StoryGenre, string>(
    GENRE_REGISTRY.map((g) => [g.enumValue, g.slug]),
);

export function genreEnumToSlug(genre: StoryGenre): string | undefined {
    // Legacy enum values (fantasy, sci_fi, …) aren't in the registry and have
    // no landing page; returning undefined makes URL builders fall back to
    // /browse, which is correct for them. Registry genres always resolve.
    return ENUM_TO_SLUG.get(genre);
}

// ─── Multi-select filter state + URL builder ─────────────
// Shared by genre-sidebar.tsx, browse/genre pages, and tests.
//
// Canonical URL shapes:
//   - No genres:        /browse?discovery=…&status=…&tag=…&tag=…
//   - One+ genres:      /genre/<slug>?genre=…&status=…&tag=…
//
// Genre always anchors the PATH (`/genre/<slug>`) so every genre keeps its
// indexable landing page; *additional* selected genres ride along as repeated
// `?genre=` query params. Status is single-select; tags are multi-select.

/** The full set of active filters for the browse / genre surfaces. */
export interface FilterState {
    view?: "all";
    genres: StoryGenre[];
    discovery?: CrownDiscoveryFilter;
    status?: StoryStatus;
    tags: string[];
    q?: string;
}

type FilterToggle =
    | { genre: StoryGenre }
    | { discovery: CrownDiscoveryFilter }
    | { status: StoryStatus }
    | { tag: string };

/**
 * Merge a genre page's path (anchor) genre with any extra `?genre=` genres
 * into one deduped list, anchor first. The anchor always leads so it stays the
 * stable path genre even if a duplicate arrives via the query string.
 */
export function mergeGenreSelection(
    anchor: StoryGenre,
    queryGenres: StoryGenre[],
): StoryGenre[] {
    return [anchor, ...queryGenres.filter((g) => g !== anchor)];
}

/**
 * Apply one add/remove (or set/clear for status) toggle to a filter state.
 * Pure and immutable — never mutates the input. Internal: callers use
 * `toggleFilterUrl`.
 */
function toggleFilter(
    state: FilterState,
    toggle: FilterToggle,
): FilterState {
    if ("genre" in toggle) {
        const has = state.genres.includes(toggle.genre);
        return {
            ...state,
            genres: has
                ? state.genres.filter((g) => g !== toggle.genre)
                : [...state.genres, toggle.genre],
        };
    }
    if ("status" in toggle) {
        return {
            ...state,
            status: state.status === toggle.status ? undefined : toggle.status,
        };
    }
    if ("discovery" in toggle) {
        return {
            ...state,
            discovery:
                state.discovery === toggle.discovery
                    ? undefined
                    : toggle.discovery,
        };
    }
    const has = state.tags.includes(toggle.tag);
    return {
        ...state,
        tags: has
            ? state.tags.filter((t) => t !== toggle.tag)
            : [...state.tags, toggle.tag],
    };
}

/**
 * Render a filter state to its canonical URL.
 *
 * `anchor` is the current page's path genre — when it's still selected it
 * stays in the path so toggling an unrelated filter doesn't bounce the reader
 * to a different genre page. Otherwise the first selected genre (deterministic
 * order) anchors the path and the rest become `?genre=` params. With no genres
 * selected the surface is `/browse`.
 */
export function renderFilterUrl(
    state: FilterState,
    anchor?: StoryGenre,
): string {
    const genres = [...new Set(state.genres)];

    let primary: StoryGenre | undefined;
    if (anchor && genres.includes(anchor) && genreEnumToSlug(anchor)) {
        primary = anchor;
    } else {
        primary = [...genres]
            .sort()
            .find((g) => genreEnumToSlug(g) !== undefined);
    }

    const params = new URLSearchParams();
    // Crown discovery is intentionally a /browse facet. Genre landing pages
    // keep their own canonical discovery model, so choosing a genre drops it.
    if (state.view === "all") params.set("view", "all");
    if (!primary && state.discovery) {
        params.set("discovery", state.discovery);
    }
    if (state.status) params.set("status", state.status);
    // Extra genres (everything but the path primary), sorted for a stable URL.
    for (const g of genres.filter((g) => g !== primary).sort()) {
        params.append("genre", g);
    }
    for (const t of [...new Set(state.tags)].sort()) {
        params.append("tag", t);
    }
    const q = state.q?.trim();
    if (q) params.set("q", q);
    const qs = params.toString();

    if (primary) {
        const slug = genreEnumToSlug(primary)!;
        return qs ? `/genre/${slug}?${qs}` : `/genre/${slug}`;
    }
    return qs ? `/browse?${qs}` : "/browse";
}

/** Toggle one filter on the current state and return the resulting URL. */
export function toggleFilterUrl(
    state: FilterState,
    toggle: FilterToggle,
    anchor?: StoryGenre,
): string {
    return renderFilterUrl(toggleFilter(state, toggle), anchor);
}
